/*
 * Copyright (c) 2021-2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */
import {navigate as navigatePage} from "wouter/use-browser-location";
import {getIconURL} from "./components/Icons/DisplayIcon.jsx";
import Objects from 'toolbox/objects';
import Fetch from "toolbox/fetch";
import {getUIGeneratorStore} from "./stores/UIGeneratorStore.jsx";

const {
    addNewUIElement,
    removeUIElement,
    updateUIElementState,
    getAllUIElements,
    clearAllUIElements,
    getUIElement
} = getUIGeneratorStore();

/**
 * This component allows to spawn drawers and modals
 */
const UI = function () {


    /**
     * This function gets displayed in the body of the object
     * @callback contentCallback
     * @param {object} inputState  - The internal state object of the element
     * @param {function} setState  - A function to set the internal state of the element
     * @param {function} close  - A function to coles the element
     */

    /**
     * This function gets triggered if the [X] order [exist] button is pressed
     * @callback closeCallback
     * @param {object} inputState  - The internal state object of the element
     * @param {function} close  - A function to coles the element
     */

    /**
     * Allows to control a modal instance
     * @param id {string} - The unique id of the drawer (uiId) example: uuid() or '1111'
     * @param content {contentCallback}  - A function witch get displayed
     * @param settings - A object with all the settings
     * @return {{setState: setState, getState: getState, close: close, open: open}}
     */
    function modal(id, content, settings) {

        function open(initState) {
            addNewUIElement(id, 'modal', content, settings?.onClose, settings)
            if ((initState !== undefined)) {
                setState(initState)
            }
        }

        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }

    function cursor(id, iconName, startPos, allowClick, lockY, lockX, color, size) {

        function open() {
            (addNewUIElement(id, 'fakeCursor', "", () => {
            }, {
                iconName: iconName,
                startPos: startPos,
                allowClick: allowClick,
                lockY: lockY,
                lockX: lockX,
                color: color,
                size: size,
            }))
        }


        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }


    function systemCursor(id, querySelector, iconName, color, size) {

        /**
         * Creates a new element.
         */
        function open() {
            (addNewUIElement(id, 'systemCursor', "", () => {
            }, {
                iconName: iconName,
                querySelector: querySelector,
                color: color,
                size: size,
            }))
        }


        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }

    /**
     * Allows to control a drawer instance
     * @param {string} id
     * @param {any} content
     * @param {any} settings
     */
    function drawer(id, content, settings) {

        function open(initState) {
            addNewUIElement(id, 'drawer', content, settings?.onClose, settings)
            if ((initState !== undefined)) {
                setState(initState)
            }
        }


        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }

    /**
     *
     * @param id
     * @param content
     * @param settings
     * @return {{getState: (function(): *), setState: setState, close: close, open: open}}
     */
    function miniDialog(id, content, settings) {

        function open(initState) {
            (addNewUIElement(id, 'miniDialog', content, settings?.onClose, settings))

            if ((initState !== undefined)) {
                setState(initState)
            }
        }

        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }

    /**
     *
     * @param id
     * @param content
     * @param settings
     * @param callbackOnClose
     * @return {{getState: (function(): *), setState: setState, close: close, open: open}}
     */
    function dropdown(id, content, settings, callbackOnClose) {

        function open(initState) {
            (addNewUIElement(id, 'dropdown', content, callbackOnClose, settings))

            if ((initState !== undefined)) {
                setState(initState)
            }
        }

        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }

    function smallWindow(id, content, settings, callbackOnClose) {

        function open(initState) {
            (addNewUIElement(id, 'smallWindow', content, callbackOnClose, settings))

            if ((initState !== undefined)) {
                setState(initState)
            }
        }

        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }

    /**
     *
     * @param id
     * @param button
     * @param settings
     * @param callbackOnClose
     * @return {{getState: (function(): *), setState: setState, close: close, open: open}}
     */
    function fixedDialog(id, button, settings, callbackOnClose) {

        function open(initState) {
            (addNewUIElement(id, 'fixedDialog', button, callbackOnClose, settings))

            if ((initState !== undefined)) {
                setState(initState)
            }
        }


        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }

    function infoDialog(id, button, settings, callbackOnClose) {

        function open(initState) {
            (addNewUIElement(id, 'infoDialog', button, callbackOnClose, settings))

            if ((initState !== undefined)) {
                setState(initState)
            }
        }


        function close() {
            return removeUIElement(id)
        }

        function setState(newState) {
            return updateUIElementState(id, newState)
        }

        function getState() {
            return getUIGeneratorStore().getUIElement(id)
        }

        return {
            open: open,
            close: close,
            setState: setState,
            getState: getState
        }
    }

    /**
     *
     */
    function page() {

        /**
         * Navigate to a new page
         * @param {object} settings - Settings object with optional pathname, search, state
         * @param {function} afterNavigate
         */
        function navigate(settings, afterNavigate = null) {
            let current = getCurrentPath()
            console.trace(settings);
            if (Objects.isObject(settings.search)) {
                settings.search = Fetch.getQueryString(settings.search)
            }

            let targetURL = (settings.pathname ?? current) + (settings.search ?? "")

            if (settings.newTab) {
                window.open(targetURL, '_blank', 'noopener,noreferrer')
            } else {
                navigatePage(targetURL);
            }
            afterNavigate?.()

            //
            // let [menuItems, layouts] = await Promise.all([Application.Menu.getMenu(), Application.Structure.getStructure()])
            //
            // let pathName = settings.pathname ?? current
            // let layout = layouts.find(item => (item.URL === pathName.replace("/a", "")))
            // let parentFolder = menuItems.find(item => (item.layoutId === layout?.indexKey))
            //
            // if (parentFolder !== undefined) {
            //     parentFolder.parentId ??= "root"
            //     // find the parent folder
            //     while (parentFolder.parentId !== "root") {
            //         parentFolder = menuItems.find(item => (item.indexKey === parentFolder.parentId))
            //         parentFolder.parentId ??= "root"
            //     }
            // }
            // let tabGroupId = parentFolder?.indexKey ?? "main"
            //
            //
            // let currentTabGroupId = window?.name ?? "main"
            //
            //
            // let isEditorPage = ["/menu", "/table"].includes(pathName) || pathName.startsWith(Paths.layout) || pathName.startsWith(Paths.process) || pathName.startsWith(Paths.reports)
            //
            // if (isEditorPage) {
            //     tabGroupId = "admin"
            //
            //     // if (pathName.startsWith(Paths.layout)) {
            //     //     tabGroupId = "admin::layout"
            //     // }
            //     //
            //     // if (pathName.startsWith(Paths.process)) {
            //     //     tabGroupId = "admin::process"
            //     // }
            //     //
            //     // if (pathName.startsWith(Paths.reports)) {
            //     //     tabGroupId = "admin::report"
            //     // }
            // }
            //
            // if (Objects.isObject(settings.search)) {
            //     settings.search = Fetch.getQueryString(settings.search)
            // }
            //
            // let targetURL = (settings.pathname ?? current) + (settings.search ?? "")
            //
            // if (Local.hasElectron()) {
            //     Local.send("openNewTab", targetURL, tabGroupId)
            // } else {
            //     // the ui test should always stay in the same tab
            //     if (current === pathName || tabGroupId === currentTabGroupId || LoginSystem.isExplorer()) {
            //         navigatePage(targetURL);
            //     } else {
            //         BrowserTabs.navigateInTab(targetURL, tabGroupId)
            //     }
            // }
            // afterNavigate?.()
        }

        /**
         * Open an new page in a new tab
         * @param {object} settings - Settings object with optional pathname, search, state
         * @param {function} afterNavigate
         */
        function navigateNewTab(settings, afterNavigate = null) {
            let current = getCurrentPath()

            if (Objects.isObject(settings.search)) {
                settings.search = Fetch.getQueryString(settings.search)
            }

            window.open((settings.pathname ?? current) + (settings.search ?? ""), '_blank', 'noopener,noreferrer')
            afterNavigate?.()
        }

        function close(id) {
            removeUIElement(id)
        }

        function getURL() {
            let url = (new URL(document.location))
            return url
        }

        function getCurrentPath() {
            // let url = getURL()
            //
            // let currentPath = decodeURI(url.pathname)

            return decodeURI(window.location.pathname)
        }

        function getURLSearchParams() {
            let url = getURL()
            let params = new URLSearchParams(url.search);


            return params
        }

        /**
         *
         * @param pageName
         * @param iconName
         */
        function setTitle(pageName, iconName) {

            if (pageName !== undefined && pageName !== "undefined") {
                document.title = pageName
            } else if (document.tenantName !== undefined && document.tenantName !== "undefined") {
                document.title = document.tenantName
            }


            if (iconName !== undefined) {
                let link = document.querySelector("link[rel*='icon']") || document.createElement('link');
                link.type = 'image/x-icon';
                link.rel = 'shortcut icon';
                link.href = getIconURL(iconName);
                document.getElementsByTagName('head')[0].appendChild(link);
            }
        }

        function setURLQuery(name, value = "", type = "replace") {
            const url = new URL(window.location.href);

            if (value) {
                url.searchParams.set(name, value);
            } else {
                url.searchParams.delete(name);
            }

            const newUrl = url.toString();
            const currentUrl = window.location.href;

            // skip the URL change hasn't changed
            if (newUrl === currentUrl) {
                return;
            }

            if (type === "replace") {
                window.history.replaceState(null, "", newUrl);
            } else if (type === "push") {
                window.history.pushState(null, "", newUrl);
            }
        }

        function onPageChange(callback) {

            const events = [
                "popstate",
                "pushState",
                "replaceState",
                "hashchange",
            ];

            const newCallback = (event) => {
                let path = getCurrentPath()
                callback(path, event)
            }

            for (const event of events) {
                window.addEventListener(event, newCallback);
            }
            return () => {
                for (const event of events) {
                    window.removeEventListener(event, newCallback);
                }
            };


        }


        function goBack() {
            window.history.back()
        }


        return {
            navigate: navigate,
            navigateNewTab: navigateNewTab,
            close: close,
            onPageChange: onPageChange,
            setTitle: setTitle,
            setURLQuery: setURLQuery,
            getURL: getURL,
            goBack: goBack,
            getCurrentPath: getCurrentPath,
            getURLSearchParams: getURLSearchParams,
        }

    }

    function camera() {

        /**
         *
         * @param id
         * @param {object} settings - Settings object with optional pathname, search, state
         * @param onCapture
         */
        function captureImage(id, settings, onCapture) {
            addNewUIElement(id, 'cameraCaptureImage', null, onCapture, settings)
        }

        function close(id) {
            return removeUIElement(id)
        }


        return {
            captureImage: captureImage,
            close: close,
        }

    }


    function confetti(root, config = {}) {

        // https://daniel-lundin.github.io/react-dom-confetti/


        function createElements(root, elementCount, colors, width, height) {
            return Array.from({length: elementCount}).map((_, index) => {
                const element = document.createElement("div");
                const color = colors[index % colors.length];
                element.style["background-color"] = color; // eslint-disable-line space-infix-ops
                element.style.width = width;
                element.style.height = height;
                element.style.position = "absolute";
                element.style.willChange = "transform, opacity";
                element.style.visibility = "hidden";
                element.style.zIndex = 999;
                root.appendChild(element);
                return element;
            });
        }

        function randomPhysics(angle, spread, startVelocity, random) {
            const radAngle = angle * (Math.PI / 180);
            const radSpread = spread * (Math.PI / 180);
            return {
                x: 0,
                y: 0,
                z: 0,
                wobble: random() * 10,
                wobbleSpeed: 0.1 + random() * 0.1,
                velocity: startVelocity * 0.5 + random() * startVelocity,
                angle2D: -radAngle + (0.5 * radSpread - random() * radSpread),
                angle3D: -(Math.PI / 4) + random() * (Math.PI / 2),
                tiltAngle: random() * Math.PI,
                tiltAngleSpeed: 0.1 + random() * 0.3
            };
        }

        function updateFetti(fetti, progress, dragFriction, decay) {
            /* eslint-disable no-param-reassign */
            fetti.physics.x += Math.cos(fetti.physics.angle2D) * fetti.physics.velocity;
            fetti.physics.y += Math.sin(fetti.physics.angle2D) * fetti.physics.velocity;
            fetti.physics.z += Math.sin(fetti.physics.angle3D) * fetti.physics.velocity;
            fetti.physics.wobble += fetti.physics.wobbleSpeed;
            // Backward compatibility
            if (decay) {
                fetti.physics.velocity *= decay;
            } else {
                fetti.physics.velocity -= fetti.physics.velocity * dragFriction;
            }
            fetti.physics.y += 3;
            fetti.physics.tiltAngle += fetti.physics.tiltAngleSpeed;

            const {x, y, z, tiltAngle, wobble} = fetti.physics;
            const wobbleX = x + 10 * Math.cos(wobble);
            const wobbleY = y + 10 * Math.sin(wobble);
            const transform = `translate3d(${wobbleX}px, ${wobbleY}px, ${z}px) rotate3d(1, 1, 1, ${tiltAngle}rad)`;

            fetti.element.style.visibility = "visible";
            fetti.element.style.transform = transform;
            fetti.element.style.opacity = 1 - progress;

            /* eslint-enable */
        }

        function animate(root, fettis, dragFriction, decay, duration, stagger) {
            let startTime;

            return new Promise(resolve => {
                function update(time) {
                    if (!startTime) startTime = time;
                    const elapsed = time - startTime;
                    const progress = startTime === time ? 0 : (time - startTime) / duration;
                    fettis.slice(0, Math.ceil(elapsed / stagger)).forEach(fetti => {
                        updateFetti(fetti, progress, dragFriction, decay);
                    });

                    if (time - startTime < duration) {
                        requestAnimationFrame(update);
                    } else {
                        fettis.forEach(fetti => {
                            if (fetti.element.parentNode === root) {
                                return root.removeChild(fetti.element);
                            }
                        });
                        resolve();
                    }
                }

                requestAnimationFrame(update);
            });
        }

        function backwardPatch(config) {
            if (!config.stagger && config.delay) {
                config.stagger = config.delay;
            }
            return config;
        }

        const {
            elementCount,
            colors,
            width,
            height,
            perspective,
            angle,
            spread,
            startVelocity,
            decay,
            dragFriction,
            duration,
            stagger,
        } = Object.assign({}, {}, backwardPatch(config));
        root.style.perspective = perspective;
        const elements = createElements(root, elementCount, colors, width, height);
        const fettis = elements.map(element => ({
            element,
            physics: randomPhysics(angle, spread, startVelocity, () => (Math.random()))
        }));

        return animate(root, fettis, dragFriction, decay, duration, stagger);


    }

    function snow(root, config = {}) {

        let snowflakesCount = 200; // Snowflake count, can be overwritten by attrs
        let baseCSS = ``;


        let bodyHeightPx = 0;
        let pageHeightVh = 0;


// Creating snowflakes
        function generateSnow(snowDensity = 200) {
            snowDensity -= 1;
            const snowWrapper = root;
            snowWrapper.innerHTML = '';
            for (let i = 0; i < snowDensity; i++) {
                let board = document.createElement('div');
                board.className = "snowflake bg-white z-90";
                snowWrapper.appendChild(board);
            }
        }

        function getOrCreateCSSElement() {
            let cssElement = document.getElementById("psjs-css");
            if (cssElement) return cssElement;

            cssElement = document.createElement('style');
            cssElement.id = 'psjs-css';
            document.head.appendChild(cssElement);
            return cssElement;
        }

// Append style for each snowflake to the head
        function addCSS(rule) {
            const cssElement = getOrCreateCSSElement();
            cssElement.innerHTML = rule; // safe to use innerHTML
            document.head.appendChild(cssElement);
        }

// Math
        function randomInt(value = 100) {
            return Math.floor(Math.random() * value) + 1;
        }

        function randomIntRange(min, max) {
            min = Math.ceil(min);
            max = Math.floor(max);
            return Math.floor(Math.random() * (max - min + 1)) + min;
        }

        function getRandomArbitrary(min, max) {
            return Math.random() * (max - min) + min;
        }

// Create style for snowflake
        function generateSnowCSS(snowDensity = 200) {
            let snowflakeName = "snowflake";
            let rule = baseCSS;

            for (let i = 1; i < snowDensity; i++) {
                let randomX = Math.random() * 100; // vw
                let randomOffset = Math.random() * 10 // vw;
                let randomXEnd = randomX + randomOffset;
                let randomXEndYoyo = randomX + (randomOffset / 2);
                let randomYoyoTime = getRandomArbitrary(0.3, 0.8);
                let randomYoyoY = randomYoyoTime * pageHeightVh; // vh
                let randomScale = Math.random();
                let fallDuration = randomIntRange(10, pageHeightVh / 10 * 3); // s
                let fallDelay = randomInt(pageHeightVh / 10 * 3) * -1; // s
                let opacity = Math.random();

                rule += `
      .snowflake:nth-child(${i}) {
      z-index:1000;
        opacity: ${opacity};
        transform: translate(${randomX}vw, -10px) scale(${randomScale});
        animation: fall-${i} ${fallDuration}s ${fallDelay}s linear infinite;
      }
      @keyframes fall-${i} {
        ${randomYoyoTime * 100}% {
          transform: translate(${randomXEnd}vw, ${randomYoyoY}vh) scale(${randomScale});
        }
        to {
          transform: translate(${randomXEndYoyo}vw, ${pageHeightVh}vh) scale(${randomScale});
        }
      }
    `
            }
            addCSS(rule);
        }

// Load the rules and execute after the DOM loads
        function createSnow() {
            //setHeightVariables
            bodyHeightPx = document.body.offsetHeight;
            pageHeightVh = (100 * bodyHeightPx / window.innerHeight);
            generateSnowCSS(snowflakesCount);
            generateSnow(snowflakesCount);
        }


        snowflakesCount -= 1;
        const snowWrapper = root;
        snowWrapper.className += " relative"

        let rule = ""
        for (let i = 0; i < snowflakesCount; i++) {
            let randomX = Math.random() * 100; // vw
            let randomY = Math.random() * 100; // vw
            let randomOffset = Math.random() * 10 // vw;
            let randomXEnd = randomX + randomOffset;
            let randomXEndYoyo = randomX + (randomOffset / 2);
            let randomYoyoTime = getRandomArbitrary(0.3, 0.8);
            let randomYoyoY = randomYoyoTime * pageHeightVh; // vh
            let randomScale = Math.random();
            let fallDuration = randomIntRange(10, pageHeightVh / 10 * 3); // s
            let fallDelay = randomInt(pageHeightVh / 10 * 3) * -1; // s
            let opacity = Math.random();


            let board = document.createElement('div');
            board.className = `bg-white z-90  w-5 h-5   rounded-full blur-xs snowflake`;
            // board.style.top = randomY + "vw"
            // board.style.left = randomX + "vw"
            // board.style.scale = randomScale
            // board.style.opacity = opacity
            snowWrapper.appendChild(board);

            rule += `
      .snowflake:nth-child(${i}) {
      z-index:1000;
        opacity: ${opacity};
        transform: translate(${randomX}vw, -10px) scale(${randomScale});
        animation: fall-${i} ${fallDuration}s ${fallDelay}s linear infinite;
      }
      @keyframes fall-${i} {
        ${randomYoyoTime * 100}% {
          transform: translate(${randomXEnd}vw, ${randomYoyoY}vh) scale(${randomScale});
        }
        to {
          transform: translate(${randomXEndYoyo}vw, ${pageHeightVh}vh) scale(${randomScale});
        }
      }
    `

            addCSS(rule);
        }

    }

    /**
     * Closes all currently active UI elements
     */
    function closeAll() {
        Object.keys(getUIGeneratorStore().getAllUIElements()).forEach(uiId => {
            (removeUIElement(uiId))
        })

    }


    function openNewEmail(emailAddress, emailSubject = "", ccEmailAddress = "", bccEmailAddress = "", emailText = "") {
        let url = `mailto:${emailAddress}?none=`

        function fixURL(string) {
            string = string.replaceAll("%", "%25")
            string = string.replaceAll(" ", "%20")
            string = string.replaceAll("!", "%21")
            string = string.replaceAll("#", "%23")
            string = string.replaceAll("*", "%2A")
            string = string.replaceAll("/", "%2F")
            string = string.replaceAll("<", "%3C")
            string = string.replaceAll("?", "%3F")
            string = string.replaceAll("\n", "%0A")
            return string
        }

        if (emailSubject !== "") {
            url += `&subject=${fixURL(emailSubject)}`
        }
        if (ccEmailAddress !== "") {
            url += `&cc=${ccEmailAddress}`
        }
        if (bccEmailAddress !== "") {
            url += `&bcc=${bccEmailAddress}`
        }
        if (emailText !== "") {
            url += `&body=${fixURL(emailText)}`
        }

        window.open(url, '_blank', 'noopener,noreferrer')
    }

    return {
        modal: modal,
        cursor: cursor,
        systemCursor: systemCursor,
        camera: camera,
        closeAll: closeAll,
        openNewEmail: openNewEmail,
        dropdown: dropdown,
        smallWindow: smallWindow,
        drawer: drawer,
        confetti: confetti,
        snow: snow,
        miniDialog: miniDialog,
        fixedDialog: fixedDialog,
        infoDialog: infoDialog,
        page: page,
    }
}()


export {
    UI,
};